import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import {
  useAdminSelectionMutation,
  useLazyViewSolarEstimationAdminQuery,
} from "../../../../../redux/api/solar-estimation/solarEstimationAPI";
import { ISolarEstimationUserSelection } from "../../../../../shared/oversight-core/dtos/request-dtos/solar-estimation-user-selection-request-dto";
import {
  IViewSolarEstimationData,
  IViewSolarEstimationOutputParams,
  IViewSolarRecommendations,
} from "../../../../../shared/oversight-core/dtos/response-dtos/view-solar-estimation-response-dto";
import { ESolarSolutionType } from "../../../../../shared/oversight-core/enums/sola-solution-type";
import { EViewSolarEstimationSolarProductCategory } from "../../../../../shared/oversight-core/enums/view-solar-estimation-solar-product-category";
import { Configurations } from "../../../../../shared/oversight-core/interfaces/configurations";
import { IViewSolarEstimationSolarProductAndOtherDetails } from "../../../../../shared/oversight-core/interfaces/view-solar-estimation-solar-system";
import MaterialIcon from "../../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import Search from "../../../../../shared/oversight-core/ui-elements/search/search";
import SpinnerModal from "../../../../../shared/oversight-core/ui-elements/spinner/spinner";
import { filterByCategories } from "../../../../../shared/oversight-core/utils/filter-by-categories";
import { formatWithCommas } from "../../../../../shared/oversight-core/utils/format-with-commas";
import Helper from "../../../../../shared/oversight-core/utils/helpers";
import { sortRecommendations } from "../../../../../shared/oversight-core/utils/solar-estimation";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../../../shared/oversight-core/utils/toast";
import RecommendedSolarComponentOption from "../../../../build-system/components/recommended-solar-component-option/recommended-solar-component-option";
import SolarComponentOption from "../../../../build-system/components/solar-component-option/solar-component-option";
import SolarComponent, {
  ISelectedSolarComponent,
} from "../../../../build-system/components/solar-component/solar-component";
import UpdateHistoryModal from "../update-history-modal/update-history-modal";
import UserPreferencesModal from "../user-preferences-modal/user-preferences-modal";
import styles from "./edit-assemble-system.module.scss";

interface IProps {
  assembleSystemData: IViewSolarEstimationData | undefined;
  estimationId: string;
  estimationNo: number;
  selectedSystemOutput: IViewSolarEstimationOutputParams;
  setSelectedSystemOutput: React.Dispatch<
    React.SetStateAction<IViewSolarEstimationOutputParams>
  >;
  setIsResetRecommendations: React.Dispatch<React.SetStateAction<boolean>>;
  submittedUser: string;
  userId: string;
  configurations: Configurations;
  estimationType: ESolarSolutionType;
}

const EditAssembleSystem = (props: IProps) => {
  const {
    assembleSystemData,
    estimationId,
    selectedSystemOutput,
    setSelectedSystemOutput,
    setIsResetRecommendations,
    submittedUser,
    userId,
    estimationNo,
    configurations,
    estimationType,
  } = props;

  const [searchValue, setSearchValue] = useState("");
  const [selectedSolarComponent, setSelectedSolarComponent] =
    useState<ISelectedSolarComponent>({
      id: 1,
      category: EViewSolarEstimationSolarProductCategory.INVERTERS,
    });
  const [isShowPreferencesModal, setIsShowPreferencesModal] = useState(false);
  const [batteries, setBatteries] = useState<
    IViewSolarEstimationSolarProductAndOtherDetails[]
  >([]);
  const [panels, setPanels] = useState<
    IViewSolarEstimationSolarProductAndOtherDetails[]
  >([]);
  const [inverters, setInverters] = useState<
    IViewSolarEstimationSolarProductAndOtherDetails[]
  >([]);
  const [waterHeaterControllers, setWaterHeaterControllers] = useState<
    IViewSolarEstimationSolarProductAndOtherDetails[]
  >([]);
  const [waterHeaters, setWaterHeaters] = useState<
    IViewSolarEstimationSolarProductAndOtherDetails[]
  >([]);
  const [recommendations, setRecommendations] = useState<
    IViewSolarRecommendations[]
  >([]);
  const [selectedComponent, setSelectedComponent] =
    useState<IViewSolarEstimationSolarProductAndOtherDetails>();
  const [selectedInverter, setSelectedInverter] =
    useState<IViewSolarEstimationSolarProductAndOtherDetails>();
  const [selectedBattery, setSelectedBattery] =
    useState<IViewSolarEstimationSolarProductAndOtherDetails>();
  const [selectedPanel, setSelectedPanel] =
    useState<IViewSolarEstimationSolarProductAndOtherDetails>();
  const [selectedWaterHeaterController, setSelectedWaterHeaterController] =
    useState<IViewSolarEstimationSolarProductAndOtherDetails>();
  const [selectedWaterHeater, setSelectedWaterHeater] =
    useState<IViewSolarEstimationSolarProductAndOtherDetails>();
  const [userSelected, setUserSelected] = useState<
    IViewSolarEstimationSolarProductAndOtherDetails[]
  >([]);
  const [originalAssembleSystemData, setOriginalAssembleSystemData] =
    useState<IViewSolarEstimationData>();
  const [currency, setCurrency] = useState("");
  const [isShowUpdateHistoryModal, setIsShowUpdateHistoryModal] =
    useState(false);
  const [validationMessages, setValidationMessages] = useState<string[]>([]);
  const [comment, setComment] = useState("");

  const [triggerAdminSelection, { isLoading: isLoadingAdminSelection }] =
    useAdminSelectionMutation();
  const [
    triggerViewSolarEstimationAdmin,
    { isFetching: isFetchingViewSolarEstimationAdmin },
  ] = useLazyViewSolarEstimationAdminQuery();

  useEffect(() => {
    if (estimationType === ESolarSolutionType.WATER_HEATING_SYSTEM) {
      setSelectedSolarComponent({
        id: 1,
        category: EViewSolarEstimationSolarProductCategory.WATER_HEATERS,
      });
    }
  }, [estimationType]);

  useEffect(() => {
    setOriginalAssembleSystemData(assembleSystemData);
    if (assembleSystemData) {
      setInverters(assembleSystemData.recommendedProducts?.INVERTERS || []);
      setBatteries(assembleSystemData.recommendedProducts?.BATTERIES || []);
      setPanels(assembleSystemData.recommendedProducts?.PANELS || []);
      setWaterHeaterControllers(
        assembleSystemData.recommendedProducts?.WATER_HEATER_CONTROLLERS || []
      );
      setWaterHeaters(
        assembleSystemData.recommendedProducts?.WATER_HEATERS || []
      );
      setRecommendations(
        sortRecommendations(assembleSystemData.recommendations || [])
      );

      const idsOfRecommended =
        assembleSystemData.recommendations?.map(
          (recommended) => recommended.id
        ) || [];

      const userSelectedProducts =
        assembleSystemData.adminSelectedProducts &&
        assembleSystemData.adminSelectedProducts.length > 0
          ? assembleSystemData.adminSelectedProducts
          : assembleSystemData.userSelectedProducts;
      if (userSelectedProducts && userSelectedProducts.length > 0) {
        const idsOfUserSelectedProducts = userSelectedProducts.map(
          (usp) => usp.id
        );

        const setComponent = (
          category: EViewSolarEstimationSolarProductCategory,
          recommendedList: IViewSolarEstimationSolarProductAndOtherDetails[],
          selectedSolarProduct: React.Dispatch<
            React.SetStateAction<
              IViewSolarEstimationSolarProductAndOtherDetails | undefined
            >
          >,
          selectedValue:
            | IViewSolarEstimationSolarProductAndOtherDetails
            | undefined
        ) => {
          if (!selectedValue) {
            const usp = userSelectedProducts.find(
              (u) => u.category === category
            );
            if (usp) {
              const found = recommendedList.find((item) =>
                idsOfUserSelectedProducts.includes(item.product.id)
              );
              selectedSolarProduct(
                found ? { ...found, count: usp.count } : undefined
              );
            }
          }
        };

        setComponent(
          EViewSolarEstimationSolarProductCategory.INVERTERS,
          assembleSystemData.recommendedProducts?.INVERTERS || [],
          setSelectedInverter,
          selectedInverter
        );
        setComponent(
          EViewSolarEstimationSolarProductCategory.BATTERIES,
          assembleSystemData.recommendedProducts?.BATTERIES || [],
          setSelectedBattery,
          selectedBattery
        );
        setComponent(
          EViewSolarEstimationSolarProductCategory.PANELS,
          assembleSystemData.recommendedProducts?.PANELS || [],
          setSelectedPanel,
          selectedPanel
        );
        setComponent(
          EViewSolarEstimationSolarProductCategory.WATER_HEATER_CONTROLLERS,
          assembleSystemData.recommendedProducts?.WATER_HEATER_CONTROLLERS ||
            [],
          setSelectedWaterHeaterController,
          selectedWaterHeaterController
        );
        setComponent(
          EViewSolarEstimationSolarProductCategory.WATER_HEATERS,
          assembleSystemData.recommendedProducts?.WATER_HEATERS || [],
          setSelectedWaterHeater,
          selectedWaterHeater
        );
        return;
      }

      if (!selectedInverter) {
        setSelectedInverter(
          assembleSystemData.recommendedProducts?.INVERTERS.find((inverter) =>
            idsOfRecommended.includes(inverter.product.id)
          )
        );
      }

      if (!selectedBattery) {
        setSelectedBattery(
          assembleSystemData.recommendedProducts?.BATTERIES.find((battery) =>
            idsOfRecommended.includes(battery.product.id)
          )
        );
      }

      if (!selectedPanel) {
        setSelectedPanel(
          assembleSystemData.recommendedProducts?.PANELS.find((panel) =>
            idsOfRecommended.includes(panel.product.id)
          )
        );
      }

      if (!selectedWaterHeaterController) {
        setSelectedWaterHeaterController(
          assembleSystemData.recommendedProducts?.WATER_HEATER_CONTROLLERS.find(
            (controller) => idsOfRecommended.includes(controller.product.id)
          )
        );
      }

      if (!selectedWaterHeater) {
        setSelectedWaterHeater(
          assembleSystemData.recommendedProducts?.WATER_HEATERS.find(
            (waterHeater) => idsOfRecommended.includes(waterHeater.product.id)
          )
        );
      }
    }
  }, [assembleSystemData]);

  useEffect(() => {
    const getSelectedComponentByCategory = () => {
      switch (selectedSolarComponent.category) {
        case EViewSolarEstimationSolarProductCategory.INVERTERS:
          return selectedInverter;
        case EViewSolarEstimationSolarProductCategory.BATTERIES:
          return selectedBattery;
        case EViewSolarEstimationSolarProductCategory.PANELS:
          return selectedPanel;
        case EViewSolarEstimationSolarProductCategory.WATER_HEATERS:
          return selectedWaterHeater;
        default:
          return selectedWaterHeaterController;
      }
    };

    setSelectedComponent(getSelectedComponentByCategory());
  }, [
    selectedSolarComponent.category,
    selectedInverter,
    selectedBattery,
    selectedPanel,
    selectedWaterHeaterController,
    selectedWaterHeater,
  ]);

  useEffect(() => {
    const userSelections: IViewSolarEstimationSolarProductAndOtherDetails[] =
      [];

    if (selectedInverter) {
      userSelections.push({
        ...selectedInverter,
      });
    }

    if (selectedBattery) {
      userSelections.push({
        ...selectedBattery,
      });
    }

    if (selectedPanel) {
      userSelections.push({ ...selectedPanel });
    }

    if (selectedWaterHeaterController) {
      userSelections.push({
        ...selectedWaterHeaterController,
      });
    }

    if (selectedWaterHeater) {
      userSelections.push({
        ...selectedWaterHeater,
      });
    }

    setUserSelected([...userSelections]);
  }, [
    selectedInverter?.product.id,
    selectedInverter?.count,
    selectedBattery?.product.id,
    selectedBattery?.count,
    selectedPanel?.product.id,
    selectedPanel?.count,
    selectedWaterHeaterController?.product.id,
    selectedWaterHeaterController?.count,
    selectedWaterHeater?.product.id,
    selectedWaterHeater?.count,
  ]);

  useEffect(() => {
    const userSelections: ISolarEstimationUserSelection[] = userSelected.map(
      (us) => ({
        productCategory: us.product.category,
        productId: us.product.id,
        count: us.count,
        isSelected: true,
      })
    );

    if (estimationId && userSelections.length > 0) {
      triggerAdminSelection({
        estimationId: estimationId,
        userSelections,
      })
        .unwrap()
        .then((userSelectionRes) => {
          setValidationMessages(userSelectionRes.validationMessages);

          triggerViewSolarEstimationAdmin({
            estimationId: estimationId,
          })
            .unwrap()
            .then((res) => {
              setComment(res.estimationView.userComment || "");
              setSelectedSystemOutput(
                res.estimationView.data.outputParams || {
                  totalWattHours: 0,
                  totalCost: 0,
                  systemVoltage: 0,
                  totalAmpHours: 0,
                  nightLoadAmpHours: 0,
                  batteryAmpHours: 0,
                  maxPowerInWatt: 0,
                  grandTotal: 0,
                  totalAccessoriesCost: 0,
                  laborCost: 0,
                  totalLiters: 0,
                }
              );

              setCurrency(res.estimationView.currency);
            })
            .catch(() => {
              setComment("");
              setSelectedSystemOutput({
                totalWattHours: 0,
                totalCost: 0,
                systemVoltage: 0,
                totalAmpHours: 0,
                nightLoadAmpHours: 0,
                batteryAmpHours: 0,
                maxPowerInWatt: 0,
                grandTotal: 0,
                totalAccessoriesCost: 0,
                laborCost: 0,
                totalLiters: 0,
              });
              setCurrency("");
            });
        })
        .catch(() => {
          setValidationMessages([]);
          setCurrency("");
        });
    }
  }, [userSelected, estimationId]);

  const onCountChange = (
    id: string,
    count: number,
    category: EViewSolarEstimationSolarProductCategory
  ) => {
    switch (category) {
      case EViewSolarEstimationSolarProductCategory.INVERTERS:
        setSelectedInverter((ps) => {
          if (ps) {
            return {
              ...ps,
              count,
            };
          }
        });

        setInverters((ps) => {
          return ps.map((inverter) => {
            return {
              ...inverter,
              count: inverter.product.id === id ? count : inverter.count,
            };
          });
        });
        break;
      case EViewSolarEstimationSolarProductCategory.BATTERIES:
        setSelectedBattery((ps) => {
          if (ps) {
            return {
              ...ps,
              count,
            };
          }
        });
        setBatteries((ps) => {
          return ps.map((battery) => {
            return {
              ...battery,
              count: battery.product.id === id ? count : battery.count,
            };
          });
        });
        break;
      case EViewSolarEstimationSolarProductCategory.PANELS:
        setSelectedPanel((ps) => {
          if (ps) {
            return {
              ...ps,
              count,
            };
          }
        });
        setPanels((ps) => {
          return ps.map((panel) => {
            return {
              ...panel,
              count: panel.product.id === id ? count : panel.count,
            };
          });
        });
        break;
      case EViewSolarEstimationSolarProductCategory.WATER_HEATERS:
        setSelectedWaterHeater((ps) => {
          if (ps) {
            return {
              ...ps,
              count,
            };
          }
        });
        setWaterHeaters((ps) => {
          return ps.map((waterHeater) => {
            return {
              ...waterHeater,
              count: waterHeater.product.id === id ? count : waterHeater.count,
            };
          });
        });
        break;
      case EViewSolarEstimationSolarProductCategory.WATER_HEATER_CONTROLLERS:
        setSelectedWaterHeaterController((ps) => {
          if (ps) {
            return {
              ...ps,
              count,
            };
          }
        });
        setWaterHeaterControllers((ps) => {
          return ps.map((controller) => {
            return {
              ...controller,
              count: controller.product.id === id ? count : controller.count,
            };
          });
        });
        break;
    }
  };

  const onSelectComponent = (
    category: EViewSolarEstimationSolarProductCategory,
    id: string
  ) => {
    switch (category) {
      case EViewSolarEstimationSolarProductCategory.INVERTERS:
        setSelectedInverter(
          inverters.find((inverter) => inverter.product.id === id)
        );
        break;
      case EViewSolarEstimationSolarProductCategory.BATTERIES:
        setSelectedBattery(
          batteries.find((battery) => battery.product.id === id)
        );
        break;
      case EViewSolarEstimationSolarProductCategory.PANELS:
        setSelectedPanel(panels.find((panel) => panel.product.id === id));
        break;
      case EViewSolarEstimationSolarProductCategory.WATER_HEATERS:
        setSelectedWaterHeater(
          waterHeaters.find((waterHeater) => waterHeater.product.id === id)
        );
        break;
      case EViewSolarEstimationSolarProductCategory.WATER_HEATER_CONTROLLERS:
        setSelectedWaterHeaterController(
          waterHeaterControllers.find(
            (controller) => controller.product.id === id
          )
        );
        break;
    }
  };

  const onReset = (
    isShowMessage: boolean,
    category?: EViewSolarEstimationSolarProductCategory
  ) => {
    if (!category) {
      triggerAdminSelection({
        estimationId: estimationId,
        userSelections: [],
      })
        .unwrap()
        .then(() => {
          setSelectedInverter(undefined);
          setSelectedBattery(undefined);
          setSelectedPanel(undefined);
          setSelectedWaterHeaterController(undefined);
          setSelectedWaterHeater(undefined);
          setIsResetRecommendations((ps) => !ps);

          if (isShowMessage) {
            showSuccessMessage("Reset successful");
          }
        })
        .catch(() => {
          if (isShowMessage) {
            showErrorMessage("Failed to reset");
          }
        });

      return;
    }

    if (originalAssembleSystemData) {
      const idsOfRecommended =
        originalAssembleSystemData.recommendations?.map(
          (recommended) => recommended.id
        ) || [];

      switch (category) {
        case EViewSolarEstimationSolarProductCategory.INVERTERS:
          setSelectedInverter(undefined);
          setInverters([
            ...(originalAssembleSystemData.recommendedProducts?.INVERTERS ||
              []),
          ]);
          setSelectedInverter(
            originalAssembleSystemData.recommendedProducts?.INVERTERS.find(
              (inverter) => idsOfRecommended.includes(inverter.product.id)
            )
          );
          break;
        case EViewSolarEstimationSolarProductCategory.BATTERIES:
          setSelectedBattery(undefined);
          setBatteries([
            ...(originalAssembleSystemData.recommendedProducts?.BATTERIES ||
              []),
          ]);
          setSelectedBattery(
            originalAssembleSystemData.recommendedProducts?.BATTERIES.find(
              (battery) => idsOfRecommended.includes(battery.product.id)
            )
          );
          break;
        case EViewSolarEstimationSolarProductCategory.PANELS:
          setSelectedPanel(undefined);
          setPanels([
            ...(originalAssembleSystemData.recommendedProducts?.PANELS || []),
          ]);
          setSelectedPanel(
            originalAssembleSystemData.recommendedProducts?.PANELS.find(
              (panel) => idsOfRecommended.includes(panel.product.id)
            )
          );
          break;
        case EViewSolarEstimationSolarProductCategory.WATER_HEATERS:
          setSelectedWaterHeater(undefined);
          setWaterHeaters([
            ...(originalAssembleSystemData.recommendedProducts?.WATER_HEATERS ||
              []),
          ]);
          setSelectedWaterHeater(
            originalAssembleSystemData.recommendedProducts?.WATER_HEATERS.find(
              (waterHeater) => idsOfRecommended.includes(waterHeater.product.id)
            )
          );
          break;
        case EViewSolarEstimationSolarProductCategory.WATER_HEATER_CONTROLLERS:
          setSelectedWaterHeaterController(undefined);
          setWaterHeaterControllers([
            ...(originalAssembleSystemData.recommendedProducts
              ?.WATER_HEATER_CONTROLLERS || []),
          ]);
          setSelectedWaterHeaterController(
            originalAssembleSystemData.recommendedProducts?.WATER_HEATER_CONTROLLERS.find(
              (controller) => idsOfRecommended.includes(controller.product.id)
            )
          );
          break;
      }
    }
  };

  const getLabel = (): string => {
    let label;
    switch (selectedSolarComponent.category) {
      case EViewSolarEstimationSolarProductCategory.INVERTERS:
        label = "Inverter";
        break;
      case EViewSolarEstimationSolarProductCategory.BATTERIES:
        label = "Battery";
        break;
      case EViewSolarEstimationSolarProductCategory.PANELS:
        label = "Panel";
        break;
      case EViewSolarEstimationSolarProductCategory.WATER_HEATER_CONTROLLERS:
        label = "Water Heater Controller";
        break;
      case EViewSolarEstimationSolarProductCategory.WATER_HEATERS:
        label = "Water Heater";
        break;
      default:
        label = "";
    }
    return label;
  };

  const solarCategories = [
    EViewSolarEstimationSolarProductCategory.INVERTERS,
    EViewSolarEstimationSolarProductCategory.BATTERIES,
    EViewSolarEstimationSolarProductCategory.PANELS,
  ];

  const waterCategories = [
    EViewSolarEstimationSolarProductCategory.WATER_HEATERS,
    EViewSolarEstimationSolarProductCategory.WATER_HEATER_CONTROLLERS,
  ];

  const solarItems = filterByCategories(userSelected, solarCategories);

  const waterItems = filterByCategories(userSelected, waterCategories);

  return (
    <>
      <div className="px-3 mt-2">
        <Row className="align-items-center">
          <Col xl={4}>
            <Row className="align-items-center justify-content-between">
              <Col>
                <Row>
                  <Col className="text-advance-description font-size-14 font-weight-500">
                    Components Cost
                  </Col>
                </Row>
                <Row>
                  <Col className="text-label font-size-24 font-weight-600">
                    {formatWithCommas(
                      Number(Helper.roundTo2(selectedSystemOutput.totalCost))
                    )}
                    &nbsp;{currency}
                  </Col>
                </Row>
              </Col>
              <Col className="col-auto">
                <Row className="align-items-center gap-3">
                  <Col className="col-auto pe-0">
                    <div
                      className={`${styles.updateHistoryIconWrapper} bg-background-6 rounded-2 p-2 cursor-pointer`}
                      onClick={() => setIsShowUpdateHistoryModal(true)}
                    >
                      <MaterialIcon
                        icon="receipt_long"
                        color="var(--color-receipt-icon)"
                        className="bg-background-5 d-flex justify-content-center align-items-center rounded-circle "
                        size={16}
                        style={{ width: "23px", height: "23px" }}
                      />
                      {comment && (
                        <div className={styles.updateHistoryCount}>1</div>
                      )}
                    </div>
                  </Col>
                  <Col
                    className="col-auto cursor-pointer bg-background-6 rounded-2 p-2"
                    onClick={() => setIsShowPreferencesModal(true)}
                  >
                    <Row className="align-items-center">
                      <Col className="col-auto pe-2">
                        <MaterialIcon
                          icon="settings"
                          color="var(--color-receipt-icon)"
                          className="bg-background-5 d-flex justify-content-center align-items-center rounded-circle"
                          size={16}
                          style={{ width: "23px", height: "23px" }}
                        />
                      </Col>
                      <Col className="title-4 font-size-14 font-weight-500 ps-0">
                        Preferences
                      </Col>
                    </Row>
                  </Col>
                  <Col
                    className="cursor-pointer col-auto bg-background-6 rounded-2 p-2"
                    onClick={() => {
                      onReset(true);
                    }}
                  >
                    <MaterialIcon
                      icon="undo"
                      color="var(--color-solar-system-type-title)"
                      size={16}
                      className="d-flex justify-content-center align-items-center"
                      style={{ width: "23px", height: "23px" }}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row className="mt-4">
              <Col className={`${styles.componentsWrapper}`}>
                {(estimationType === ESolarSolutionType.SOLAR_ENERGY_SYSTEM ||
                  estimationType === ESolarSolutionType.BOTH) &&
                  solarItems.length > 0 && (
                    <>
                      <Row className="mb-2">
                        <Col className="text-secondary font-weight-500 font-size-14">
                          Solar Power Equipments
                        </Col>
                      </Row>
                      {solarItems.map((us, index) => {
                        const product = us.product;
                        const quantity = us.count;
                        const id = index + 1;

                        let categoryLabel = "";
                        switch (product.category) {
                          case EViewSolarEstimationSolarProductCategory.INVERTERS:
                            categoryLabel = "inverter";
                            break;
                          case EViewSolarEstimationSolarProductCategory.BATTERIES:
                            categoryLabel = "battery";
                            break;
                          case EViewSolarEstimationSolarProductCategory.PANELS:
                            categoryLabel = "panel";
                            break;
                          default:
                            categoryLabel = "controller";
                        }

                        return (
                          <Row
                            key={us.product.id}
                            className={index === 0 ? "" : "mt-2"}
                          >
                            <Col>
                              <SolarComponent
                                component={{
                                  id: id,
                                  category: product.category,
                                  name: product.name,
                                  quantity: quantity,
                                  value: product.price,
                                  unit: product.currency,
                                }}
                                selected={selectedSolarComponent}
                                onSelect={(
                                  id: number,
                                  category: EViewSolarEstimationSolarProductCategory
                                ) => {
                                  setSelectedSolarComponent({ id, category });
                                }}
                                isWarning={validationMessages.some((msg) =>
                                  msg.includes(categoryLabel)
                                )}
                              />
                            </Col>
                          </Row>
                        );
                      })}
                    </>
                  )}
                {(estimationType === ESolarSolutionType.BOTH ||
                  estimationType === ESolarSolutionType.WATER_HEATING_SYSTEM) &&
                  waterItems.length > 0 && (
                    <>
                      <Row className="mt-4 mb-2">
                        <Col className="text-secondary font-weight-500 font-size-14">
                          Water Heater Equipments
                        </Col>
                      </Row>
                      {waterItems.map((us, index) => {
                        const product = us.product;
                        const quantity = us.count;
                        const id = solarItems.length + index + 1;

                        return (
                          <Row
                            key={`${product.id}-${product.category}`}
                            className={index === 0 ? "" : "mt-2"}
                          >
                            <Col>
                              <SolarComponent
                                component={{
                                  id,
                                  category: product.category,
                                  name: product.name,
                                  quantity,
                                  value: product.price,
                                  unit: product.currency,
                                }}
                                selected={selectedSolarComponent}
                                onSelect={(
                                  id: number,
                                  category: EViewSolarEstimationSolarProductCategory
                                ) => {
                                  setSelectedSolarComponent({
                                    id,
                                    category,
                                  });
                                }}
                                isWarning={validationMessages.some((msg) => {
                                  const category = product.category;
                                  const getCategoryName = () =>
                                    category ===
                                    EViewSolarEstimationSolarProductCategory.WATER_HEATERS
                                      ? "water heater"
                                      : "";
                                  return msg.includes(getCategoryName());
                                })}
                              />
                            </Col>
                          </Row>
                        );
                      })}
                    </>
                  )}
              </Col>
            </Row>
          </Col>
          <Col>
            <Row className="align-items-center">
              <Col className="text-label font-size-20 font-weight-600">
                Select&nbsp;{getLabel()}
              </Col>
              <Col xl={4}>
                <Search
                  placeholder={`Search ${getLabel()}`}
                  handleSearchChange={(searchText: string) => {
                    setSearchValue(searchText);
                  }}
                  onSearch={() => {
                    console.log();
                  }}
                  value={searchValue}
                  isHideSearchButton
                />
              </Col>
            </Row>
            <Row className="mt-4">
              <Col className={`${styles.componentsWrapper}`}>
                {selectedComponent && (
                  <Row>
                    <Col>
                      <RecommendedSolarComponentOption
                        id={selectedComponent.product.id}
                        name={selectedComponent.product.name}
                        category={selectedComponent.product.category}
                        maxOperatingVoltage={
                          selectedComponent.product.maxOperatingVoltage
                        }
                        price={selectedComponent.product.price}
                        currency={selectedComponent.product.currency}
                        quantity={selectedComponent.count}
                        liters={selectedComponent.product.liters}
                        ratedPower={selectedComponent.product.ratedPower}
                        amperage={selectedComponent.product.amperage}
                        batteryVoltage={
                          selectedComponent.product.batteryVoltage
                        }
                        isRecommended={recommendations
                          .map((r) => r.id)
                          .includes(selectedComponent.product.id)}
                        onCountChange={onCountChange}
                        validationMessage={validationMessages.find((msg) => {
                          const category = selectedComponent.product.category;

                          if (
                            category ===
                            EViewSolarEstimationSolarProductCategory.INVERTERS
                          ) {
                            return msg.includes("inverter");
                          }

                          if (
                            category ===
                            EViewSolarEstimationSolarProductCategory.BATTERIES
                          ) {
                            return msg.includes("battery");
                          }

                          if (
                            category ===
                            EViewSolarEstimationSolarProductCategory.PANELS
                          ) {
                            return msg.includes("panel");
                          }
                        })}
                      />
                    </Col>
                  </Row>
                )}
                <Row className="mt-4">
                  <Col className="text-advance-description font-size-14 font-weight-500">
                    All Options
                  </Col>
                  <Col
                    className="col-auto cursor-pointer"
                    onClick={() => {
                      onReset(false, selectedSolarComponent.category);
                    }}
                  >
                    <MaterialIcon
                      icon="undo"
                      color="#1A3C84"
                      size={16}
                      className="bg-background-9 p-2 rounded-3"
                    />
                  </Col>
                </Row>
                {(() => {
                  let options: IViewSolarEstimationSolarProductAndOtherDetails[] =
                    [];
                  if (
                    selectedSolarComponent.category ===
                    EViewSolarEstimationSolarProductCategory.INVERTERS
                  ) {
                    options = inverters;
                  } else if (
                    selectedSolarComponent.category ===
                    EViewSolarEstimationSolarProductCategory.BATTERIES
                  ) {
                    options = batteries;
                  } else if (
                    selectedSolarComponent.category ===
                    EViewSolarEstimationSolarProductCategory.PANELS
                  ) {
                    options = panels;
                  } else if (
                    selectedSolarComponent.category ===
                    EViewSolarEstimationSolarProductCategory.WATER_HEATERS
                  ) {
                    options = waterHeaters;
                  } else {
                    options = waterHeaterControllers;
                  }
                  return (
                    <Row className="g-3">
                      {options
                        .filter((comp) =>
                          comp.product.name
                            .toLowerCase()
                            .includes(searchValue.toLowerCase())
                        )
                        .map((comp) => {
                          const product = comp.product;

                          return (
                            <Col key={product.id} className="col-auto">
                              <SolarComponentOption
                                selected={selectedComponent?.product.id || ""}
                                id={product.id}
                                isRecommended={recommendations
                                  .map((r) => r.id)
                                  .includes(product.id)}
                                category={product.category}
                                name={product.name}
                                maxOperatingVoltage={
                                  product.maxOperatingVoltage
                                }
                                unit={product.currency}
                                price={product.price}
                                liters={product.liters}
                                ratedPower={product.ratedPower}
                                amperage={product.amperage}
                                batteryVoltage={product.batteryVoltage}
                                onSelect={onSelectComponent}
                              />
                            </Col>
                          );
                        })}
                    </Row>
                  );
                })()}
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
      <UserPreferencesModal
        show={isShowPreferencesModal}
        onCancel={() => {
          setIsShowPreferencesModal(false);
        }}
        onClose={() => {
          setIsShowPreferencesModal(false);
        }}
        userId={userId}
        configurations={configurations}
      />
      <UpdateHistoryModal
        show={isShowUpdateHistoryModal}
        onClose={() => setIsShowUpdateHistoryModal(false)}
        estimationId={estimationId}
        user={submittedUser}
        currency={currency}
        estimationNo={estimationNo}
      />
      <SpinnerModal
        show={isLoadingAdminSelection || isFetchingViewSolarEstimationAdmin}
      />
    </>
  );
};

export default EditAssembleSystem;
